import React, { ComponentType } from 'react';

import styled from 'styled-components';

import { Card } from '@components/card';
import { IconProps } from '@components/icons';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export type LabelFieldTableProps = {
  children: React.ReactNode;
};

export const LabelFieldTable = ({ children }: LabelFieldTableProps) => {
  return (
    <Card space={2} variant="gray">
      <StyledTable>{children}</StyledTable>
    </Card>
  );
};

const border = {
  borderBottom: `1px solid ${theme.color.border10}`,
};

const StyledTable = styled.table({
  tableLayout: 'fixed',
  width: '100%',
  color: theme.color.textSecondary,

  'th, td': {
    padding: `${pxToRem(8)} ${pxToRem(0)}`,
  },
  thead: {
    th: border,
  },
  'tr:not(:last-child)': {
    td: border,
  },
});

const iconProps = {
  size: 16,
  color: 'purple50' as Color,
};

const HeaderRow: React.FC = ({ children }) => {
  return (
    <thead>
      <tr>{children}</tr>
    </thead>
  );
};

type HeaderColumnProps = { label: string; icon: (props: IconProps) => JSX.Element };

const HeaderColumn = ({ label, icon: Icon }: HeaderColumnProps) => (
  <th>
    <Stack orientation="horizontal" space={1} align="center">
      <Icon {...iconProps} />
      <Text text={label} font="semibold14" />
    </Stack>
  </th>
);

type RowProps = {
  icon: ComponentType<IconProps>;
  label: string;
  id: string;
};

const Row: React.FC<RowProps> = ({ icon: Icon, label, id, children }) => {
  return (
    <tr key={id}>
      <td>
        <Stack orientation="horizontal" align="center" space={2} style={{ overflow: 'hidden' }}>
          <Icon {...iconProps} />
          <Text text={label} font="regular14" ellipsis />
        </Stack>
      </td>
      <td>{children}</td>
    </tr>
  );
};

LabelFieldTable.HeaderRow = HeaderRow;
LabelFieldTable.HeaderColumn = HeaderColumn;
LabelFieldTable.Row = Row;
